import React, { useState, useEffect } from "react";
import {
  Page,
  Card,
  DataTable,
  Text,
  BlockStack,
  Select,
  ProgressBar,
  Box,
  Divider,
  Layout,
} from "@shopify/polaris";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import FilterBar from "../../Components/Analytics/FilterBar";
import analyticsApi from "../../Services/analyticsApi";

const COLORS = ["#5C6AC4", "#47C1BF", "#F49342", "#DC3545", "#00A6A0", "#9C6ADE"];

const Customers = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    include_manual: false,
    repeat_type: "basic",
  });
  const [ltvData, setLtvData] = useState(null);
  const [repeatData, setRepeatData] = useState(null);
  const [cohortData, setCohortData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [ltvResponse, repeatResponse, cohortResponse] = await Promise.all([
        analyticsApi.getCustomerLTV(filters),
        analyticsApi.getRepeatCustomers(filters),
        analyticsApi.getCustomerCohorts(filters),
      ]);

      setLtvData(ltvResponse.data.data);
      setRepeatData(repeatResponse.data.data);
      setCohortData(cohortResponse.data.data);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleRepeatTypeChange = (value) => {
    setFilters((prev) => ({ ...prev, repeat_type: value }));
  };

  if (isLoading) {
    return (
      <Page title="Customer Analytics" backAction={{ content: "Back", url: "/admin/analytics" }}>
        <BlockStack gap="400">
          <Card>
            <Box padding="400">
              <Text>Loading customer data...</Text>
            </Box>
          </Card>
        </BlockStack>
      </Page>
    );
  }

  // Prepare LTV data for chart
  const ltvChartData = ltvData?.top_customers?.slice(0, 10).map((customer) => ({
    name: customer.name || "N/A",
    ltv: customer.lifetime_value,
    purchases: customer.total_purchases,
  })) || [];

  // Prepare LTV data for table (using phone instead of email)
  const ltvRows = ltvData?.top_customers?.map((customer) => [
    customer.name || "N/A",
    customer.phone || "N/A",
    customer.total_purchases,
    `${customer.lifetime_value.toFixed(2)} KWD`,
    new Date(customer.first_purchase).toLocaleDateString(),
    new Date(customer.last_purchase).toLocaleDateString(),
  ]) || [];

  // Prepare cohort data for chart
  const cohortChartData = cohortData?.cohorts?.map((cohort) => ({
    month: cohort.cohort_month,
    customers: cohort.customers,
    revenue: cohort.total_revenue,
    avgRevenue: cohort.average_revenue_per_customer,
  })) || [];

  // Prepare cohort data for table
  const cohortRows = cohortData?.cohorts?.map((cohort) => [
    cohort.cohort_month,
    cohort.customers,
    `${cohort.total_revenue.toFixed(2)} KWD`,
    `${cohort.average_revenue_per_customer.toFixed(2)} KWD`,
  ]) || [];

  // Get repeat customers based on type (using phone instead of email)
  const getRepeatCustomersData = () => {
    if (!repeatData?.results) return [];

    const results = repeatData.results;

    if (filters.repeat_type === "basic" && results.basic) {
      return results.basic.customers.map((customer) => [
        customer.name || "N/A",
        customer.phone || "N/A",
        customer.purchase_count,
        `${customer.lifetime_value.toFixed(2)} KWD`,
        new Date(customer.first_purchase).toLocaleDateString(),
        new Date(customer.last_purchase).toLocaleDateString(),
      ]);
    }

    if (filters.repeat_type === "time_based" && results.time_based) {
      return results.time_based.customers.map((customer) => [
        customer.name || "N/A",
        customer.phone || "N/A",
        customer.purchase_count,
        `${customer.lifetime_value.toFixed(2)} KWD`,
        `${customer.days_between_purchases} days`,
        new Date(customer.last_purchase).toLocaleDateString(),
      ]);
    }

    if (filters.repeat_type === "same_type" && results.same_type) {
      return [
        [
          "Multiple Courses",
          results.same_type.multiple_courses_count,
          "-",
          "-",
          "-",
          "-",
        ],
        [
          "Multiple Lectures",
          results.same_type.multiple_lectures_count,
          "-",
          "-",
          "-",
          "-",
        ],
      ];
    }

    return [];
  };

  const repeatCustomersRows = getRepeatCustomersData();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>
            {payload[0].payload.name || payload[0].payload.month}
          </p>
          {payload.map((item, index) => (
            <p key={index} style={{ margin: "5px 0 0 0", color: item.color }}>
              {item.name}: {typeof item.value === "number" ? item.value.toFixed(2) : item.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Page
      title="Customer Analytics"
      subtitle="Lifetime value, repeat customers, and cohort analysis"
      backAction={{ content: "Back", url: "/admin/analytics" }}
    >
      <BlockStack gap="500">
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          isLoading={isLoading}
        />

        {/* Repeat Customers Section */}
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text variant="headingLg" as="h2">
                  Repeat Customers
                </Text>
                <div style={{ width: "250px" }}>
                  <Select
                    label="Repeat Type"
                    options={[
                      { label: "Basic (2+ purchases)", value: "basic" },
                      { label: "Same Type (multiple of same)", value: "same_type" },
                      { label: "Time-Based (30+ days apart)", value: "time_based" },
                    ]}
                    value={filters.repeat_type}
                    onChange={handleRepeatTypeChange}
                  />
                </div>
              </div>
              <Divider />

              {repeatCustomersRows.length > 0 ? (
                <DataTable
                  columnContentTypes={[
                    "text",
                    "text",
                    "numeric",
                    "numeric",
                    "text",
                    "text",
                  ]}
                  headings={
                    filters.repeat_type === "time_based"
                      ? [
                          "Name",
                          "Phone",
                          "Purchases",
                          "Lifetime Value",
                          "Days Between",
                          "Last Purchase",
                        ]
                      : filters.repeat_type === "same_type"
                      ? ["Category", "Count", "-", "-", "-", "-"]
                      : [
                          "Name",
                          "Phone",
                          "Purchases",
                          "Lifetime Value",
                          "First Purchase",
                          "Last Purchase",
                        ]
                  }
                  rows={repeatCustomersRows}
                />
              ) : (
                <Text>No repeat customers found for the selected criteria</Text>
              )}
            </BlockStack>
          </Box>
        </Card>

        {/* Top Customers by Lifetime Value */}
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <Text variant="headingLg" as="h2">
                Top Customers by Lifetime Value
              </Text>
              <Text variant="bodyMd" tone="subdued">
                Average LTV: {ltvData?.average_lifetime_value?.toFixed(2) || "0.00"} KWD
              </Text>
              <Divider />

              {/* LTV Bar Chart */}
              {ltvChartData.length > 0 && (
                <div style={{ width: "100%", height: "400px" }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={ltvChartData}
                      layout="vertical"
                      margin={{ left: 120 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" style={{ fontSize: "12px" }} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={120}
                        style={{ fontSize: "11px" }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="ltv" fill={COLORS[0]} name="Lifetime Value (KWD)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* LTV with Progress Bars */}
              <Box>
                <Text variant="headingMd" as="h3" fontWeight="semibold">
                  Top 10 Customers
                </Text>
                {ltvChartData.map((customer, index) => {
                  const maxLTV = Math.max(...ltvChartData.map((c) => c.ltv));
                  const progress = (customer.ltv / maxLTV) * 100;

                  return (
                    <div key={index} style={{ marginTop: "12px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <Text variant="bodySm" as="span">
                          {customer.name}
                        </Text>
                        <Text variant="bodySm" as="span" fontWeight="semibold">
                          {customer.ltv.toFixed(2)} KWD ({customer.purchases} purchases)
                        </Text>
                      </div>
                      <ProgressBar progress={progress} size="small" />
                    </div>
                  );
                })}
              </Box>

              {/* Full LTV Table */}
              {ltvRows.length > 0 && (
                <Box>
                  <Text variant="headingMd" as="h3" fontWeight="semibold">
                    Full Customer List
                  </Text>
                  <DataTable
                    columnContentTypes={[
                      "text",
                      "text",
                      "numeric",
                      "numeric",
                      "text",
                      "text",
                    ]}
                    headings={[
                      "Name",
                      "Phone",
                      "Total Purchases",
                      "Lifetime Value",
                      "First Purchase",
                      "Last Purchase",
                    ]}
                    rows={ltvRows}
                  />
                </Box>
              )}
            </BlockStack>
          </Box>
        </Card>

        {/* Customer Cohorts */}
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <Text variant="headingLg" as="h2">
                Customer Cohorts (by First Purchase Month)
              </Text>
              <Divider />

              {/* Cohort Area Chart */}
              {cohortChartData.length > 0 && (
                <div style={{ width: "100%", height: "400px" }}>
                  <ResponsiveContainer>
                    <AreaChart data={[...cohortChartData].reverse()}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS[1]} stopOpacity={0.8} />
                          <stop offset="95%" stopColor={COLORS[1]} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorCustomers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.8} />
                          <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        style={{ fontSize: "12px" }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis style={{ fontSize: "12px" }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="customers"
                        stroke={COLORS[0]}
                        fillOpacity={1}
                        fill="url(#colorCustomers)"
                        name="Customers"
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke={COLORS[1]}
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                        name="Revenue (KWD)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {cohortRows.length > 0 ? (
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric", "numeric"]}
                  headings={[
                    "Cohort Month",
                    "Customers",
                    "Total Revenue",
                    "Avg Revenue/Customer",
                  ]}
                  rows={cohortRows}
                />
              ) : (
                <Text>No cohort data available</Text>
              )}
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
};

export default Customers;
